import { FC, useCallback, useEffect, useMemo } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Highlight from '@tiptap/extension-highlight'
import Color from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import Image from '@tiptap/extension-image'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import CharacterCount from '@tiptap/extension-character-count'
import Typography from '@tiptap/extension-typography'

const TableCellWithStyle = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style') || null,
                renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
            },
        }
    },
})

const TextDirection = Extension.create({
    name: 'textDirection',
    addGlobalAttributes() {
        return [{
            types: ['paragraph', 'heading', 'bulletList', 'orderedList', 'blockquote', 'taskList'],
            attributes: {
                dir: {
                    default: null,
                    parseHTML: (el) => el.getAttribute('dir'),
                    renderHTML: (attrs) => (attrs.dir ? { dir: attrs.dir } : {}),
                },
            },
        }]
    },
})

interface HtmlEditorProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
}

const HtmlEditor: FC<HtmlEditorProps> = ({
    value,
    onChange,
    placeholder = 'Write your content here...',
    disabled = false,
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3, 4, 5, 6] },
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
            }),
            Placeholder.configure({ placeholder }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
                alignments: ['left', 'center', 'right', 'justify'],
            }),
            Underline,
            Subscript,
            Superscript,
            Highlight.configure({ multicolor: true }),
            TextStyle,
            Color,
            Image.configure({ inline: true, allowBase64: true }),
            Table.configure({ resizable: true }),
            TableRow,
            TableCellWithStyle,
            TableHeader,
            TaskList,
            TaskItem.configure({ nested: true }),
            CharacterCount,
            Typography,
            TextDirection,
        ],
        content: value,
        editable: !disabled,
        editorProps: {
            attributes: {
                class: 'sfy-wysiwyg-editor',
            },
        },
        onUpdate: ({ editor: ed }) => {
            onChange(ed.getHTML())
        },
    })

    useEffect(() => {
        if (!editor) return
        editor.setEditable(!disabled)
    }, [editor, disabled])

    useEffect(() => {
        if (!editor || editor.isFocused) return
        const current = editor.getHTML()
        if (value !== current) {
            editor.commands.setContent(value ?? '', false)
        }
    }, [value, editor])

    const setDir = useCallback((dir: string) => {
        if (!editor) return
        for (const type of ['paragraph', 'heading', 'bulletList', 'orderedList', 'blockquote', 'taskList']) {
            try {
                editor.chain().focus().updateAttributes(type, { dir }).run()
                return
            } catch {
                // skip
            }
        }
    }, [editor])

    const addImage = useCallback(() => {
        if (!editor) return
        const url = window.prompt('Image URL:')
        if (url) editor.chain().focus().setImage({ src: url }).run()
    }, [editor])

    const addLink = useCallback(() => {
        if (!editor) return
        const url = window.prompt('URL:', 'https://')
        if (url) editor.chain().focus().setLink({ href: url }).run()
    }, [editor])

    const toolbar = useMemo(() => {
        if (!editor) return null

        const Btn = ({ onClick, active, disabled: btnDisabled, title, children }: {
            onClick: () => void
            active?: boolean
            disabled?: boolean
            title: string
            children: React.ReactNode
        }) => (
            <button
                type="button"
                onClick={onClick}
                className={active ? 'active' : ''}
                disabled={btnDisabled ?? disabled}
                title={title}
            >
                {children}
            </button>
        )

        const Divider = () => <span className="sfy-wysiwyg-toolbar-divider" />

        return (
            <div className="sfy-wysiwyg-toolbar">
                <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">↩</Btn>
                <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">↪</Btn>
                <Divider />

                <select
                    title="Block type"
                    disabled={disabled}
                    value={
                        editor.isActive('heading', { level: 1 }) ? 'h1'
                            : editor.isActive('heading', { level: 2 }) ? 'h2'
                                : editor.isActive('heading', { level: 3 }) ? 'h3'
                                    : editor.isActive('heading', { level: 4 }) ? 'h4'
                                        : editor.isActive('heading', { level: 5 }) ? 'h5'
                                            : editor.isActive('heading', { level: 6 }) ? 'h6'
                                                : editor.isActive('codeBlock') ? 'code'
                                                    : 'p'
                    }
                    onChange={(e) => {
                        const v = e.target.value
                        if (v === 'p') editor.chain().focus().setParagraph().run()
                        else if (v === 'code') editor.chain().focus().toggleCodeBlock().run()
                        else {
                            const level = parseInt(v.slice(1), 10) as 1 | 2 | 3 | 4 | 5 | 6
                            editor.chain().focus().toggleHeading({ level }).run()
                        }
                    }}
                >
                    <option value="p">Paragraph</option>
                    <option value="h1">Heading 1</option>
                    <option value="h2">Heading 2</option>
                    <option value="h3">Heading 3</option>
                    <option value="h4">Heading 4</option>
                    <option value="h5">Heading 5</option>
                    <option value="h6">Heading 6</option>
                    <option value="code">Code Block</option>
                </select>

                <Divider />

                <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><strong>B</strong></Btn>
                <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><em>I</em></Btn>
                <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><u>U</u></Btn>
                <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><s>S</s></Btn>
                <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code">&lt;/&gt;</Btn>
                <Btn onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} title="Subscript">X₂</Btn>
                <Btn onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} title="Superscript">X²</Btn>
                <Btn onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Highlight">🖍</Btn>

                <Divider />

                <label title="Text colour" style={{ display: 'inline-flex', alignItems: 'center', cursor: disabled ? 'not-allowed' : 'pointer', padding: '0.25rem', opacity: disabled ? 0.6 : 1 }}>
                    <span style={{ fontSize: '0.75rem', marginRight: 2 }}>A</span>
                    <input
                        type="color"
                        disabled={disabled}
                        style={{ width: 18, height: 18, border: 'none', padding: 0, cursor: disabled ? 'not-allowed' : 'pointer', background: 'transparent' }}
                        onInput={(e) => editor.chain().focus().setColor((e.target as HTMLInputElement).value).run()}
                        value={editor.getAttributes('textStyle').color || '#000000'}
                        title="Text colour"
                    />
                </label>
                <Btn onClick={() => editor.chain().focus().unsetColor().run()} title="Remove colour">⊘</Btn>

                <Divider />

                <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left">⫷</Btn>
                <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align centre">⫿</Btn>
                <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right">⫸</Btn>
                <Btn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify">☰</Btn>

                <Divider />

                <Btn onClick={() => setDir('ltr')} active={editor.isActive({ dir: 'ltr' })} title="Left-to-Right">LTR</Btn>
                <Btn onClick={() => setDir('rtl')} active={editor.isActive({ dir: 'rtl' })} title="Right-to-Left">RTL</Btn>

                <Divider />

                <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">•</Btn>
                <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list">1.</Btn>
                <Btn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} title="Task list">☑</Btn>

                <Divider />

                <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">&ldquo;</Btn>
                <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">―</Btn>
                <Btn onClick={() => editor.chain().focus().setHardBreak().run()} title="Line break">↵</Btn>

                <Divider />

                <Btn onClick={addLink} active={editor.isActive('link')} title="Insert link">🔗</Btn>
                {editor.isActive('link') && (
                    <Btn onClick={() => editor.chain().focus().unsetLink().run()} title="Remove link">🚫</Btn>
                )}
                <Btn onClick={addImage} title="Insert image">🖼</Btn>

                <Divider />

                <Btn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table">⊞</Btn>
                {editor.isActive('table') && (
                    <>
                        <Btn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column">Col+</Btn>
                        <Btn onClick={() => editor.chain().focus().deleteColumn().run()} title="Remove column">Col−</Btn>
                        <Btn onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row">Row+</Btn>
                        <Btn onClick={() => editor.chain().focus().deleteRow().run()} title="Remove row">Row−</Btn>
                        <Btn onClick={() => editor.chain().focus().mergeCells().run()} title="Merge cells">⊟</Btn>
                        <Btn onClick={() => editor.chain().focus().splitCell().run()} title="Split cell">⊞</Btn>
                        <Btn onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table">🗑</Btn>
                    </>
                )}

                <Divider />

                <Btn onClick={() => { editor.chain().focus().clearNodes().unsetAllMarks().run() }} title="Clear formatting">⌧</Btn>
            </div>
        )
    }, [editor, disabled, addLink, addImage, setDir])

    if (!editor) return null

    const chars = editor.storage.characterCount.characters()
    const words = editor.storage.characterCount.words()

    return (
        <div className="editor-container editor-container_classic-editor">
            <div className="editor-container__editor">
                {toolbar}
                <EditorContent editor={editor} />
                <div className="sfy-wysiwyg-statusbar">
                    {words} words · {chars} characters
                </div>
            </div>
        </div>
    )
}

export default HtmlEditor
